import React, { Suspense, lazy } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { parse } from 'query-string'

import {
  LOGIN,
  REGISTER,
  PRICING,
  DASHBOARD,
  DASHBOARD_COMPETITORS_TRACKING_ID,
  FORGOT_PASSWORD,
  EXTENSION,
  SEO_ANALYZER_SITE_AUDIT,
  UBERSUGGEST_OVERVIEW,
  UBERSUGGEST_KEYWORD_IDEAS,
  UBERSUGGEST_KEYWORD_VISUALIZATION,
  UBERSUGGEST_BULK_ANALYSIS,
  TRAFFIC_ANALYZER_KEYWORDS,
  TRAFFIC_ANALYZER_COMPETITORS,
  UBERSUGGEST_CONTENT_IDEAS,
  UBERSUGGEST_KEYWORD_LISTS,
  TRAFFIC_ANALYZER_OVERVIEW,
  TRAFFIC_ANALYZER_TOP_PAGES,
  SEO_ANALYZER_BACKLINKS,
  SEO_ANALYZER_BACKLINK_OPPORTUNITY,
  LABS_LANDING,
  ALERTS,
  ALERTS_ID,
  CHANGE_PASSWORD,
  CHECKOUT,
  WORKSPACE,
  SEO_OPPORTUNITIES,
  SETTINGS_ID,
  POSITION_TRACKING_ID,
  KEYWORD_LISTS_ID,
  VERIFY_EMAIL,
  ENTERPRISE_DASHBOARD,
} from 'utils/seo/consts/location-paths/index.mjs'

import Loading from '../../components/loading/Loading'
import ErrorBoundary from '../ErrorBoundary'
import { isAdminUser, isSignedIn } from '../../utils/userAuth'
import KeywordListsSaga from '../KeywordLists/saga'
import KeywordListsReducer from '../KeywordLists/reducer'
import SiteAuditSaga from '../SiteAudit/saga'
import SiteAuditReducer from '../SiteAudit/reducer'
import AdminDashboardReducer from '../AdministratorDashboard/reducer'
import AdminDashboardSAga from '../AdministratorDashboard/saga'
import { AIWRITER_BASE_URL } from '../../utils/consts.mjs'
import { TurnstileProvider } from '../../components/turnstile'
import { HelmetReact } from '../../components/helmetReact'

const TrafficAnalyzerOverviewContainer = lazy(() =>
  import('../TrafficAnalyzerOverview'),
)
const TopPagesContainer = lazy(() => import('../TopPages'))
const KeywordsByTrafficContainer = lazy(() => import('../KeywordByTraffic'))
const KeywordOverviewContainer = lazy(() => import('../KeywordOverview'))
const NewKeywordIdeasContainer = lazy(() => import('../NewKeywordIdeas'))
const KeywordVisualizationContainer = lazy(() =>
  import('../KeywordVisualizationContainer'),
)
const KeywordListsContainer = lazy(() => import('../KeywordLists'))
const KeywordsContainer = lazy(() => import('../KeywordLists/Keywords'))
const ContentIdeasContainer = lazy(() => import('../ContentIdeas'))
const SiteAuditContainer = lazy(() => import('../SiteAudit'))
const BackLinksContainer = lazy(() => import('../BackLinks'))
const BackLinkOpportunityContainer = lazy(() =>
  import('../BackLinkOpportunity'),
)
const RankTrackingContainer = lazy(() => import('../Dashboard/RankTracking'))
const WorkspaceContainer = lazy(() => import('../Dashboard/Workspace'))
const CompetitorTrackingContainer = lazy(() =>
  import('../Dashboard/CompetitorTracking'),
)
const SeoOpportunitiesContainer = lazy(() =>
  import('../Dashboard/SeoOpportunities'),
)
const DashboardContainer = lazy(() => import('../Dashboard'))
const SettingsContainer = lazy(() => import('../Settings'))
const CheckoutContainer = lazy(() => import('../Checkout'))
const PricingContainer = lazy(() => import('../Pricing'))
const EmailVerificationPage = lazy(() => import('../EmailVerification'))
const CompetitorAnalysisContainer = lazy(() => import('../CompetitorAnalysis'))
const LoginContainer = lazy(() => import('../Login'))
const RegisterContainer = lazy(() => import('../Register'))
const ForgotPasswordContainer = lazy(() => import('../ForgotPassword'))
const ChangePasswordContainer = lazy(() => import('../ChangePassword'))
const AlertsContainer = lazy(() => import('../Alerts'))
const AdminContainer = lazy(() => import('../Admin'))
const AlertPage = lazy(() => import('../Alerts/AlertPage'))
const ExtensionContainer = lazy(() => import('../Extension'))
const LabsContainer = lazy(() => import('../Labs'))
const KeywordsGeneratorContainer = lazy(() => import('../KeywordsGenerator'))

const AdministratorDashboardContainer = lazy(() =>
  import('../AdministratorDashboard'),
)
const BulkAnalysisContainer = lazy(() => import('features/bulk-analysis'))

const AuthRoute = ({
  user,
  render,
  languageCode,
  role = 'user',
  redirectTo,
  ...rest
}) => {
  const isLoggedIn =
    role === 'user' ? isSignedIn() : isSignedIn() && isAdminUser(user)

  return (
    <Route
      {...rest}
      render={(props) => {
        const redirectToRegisterOrLogin = props.location.search.includes(
          'aiwAddon=',
        )
          ? `/${languageCode}/register`
          : `/${languageCode}/login`

        return isLoggedIn ? (
          render(props)
        ) : (
          <Redirect
            to={{
              pathname: redirectTo ?? redirectToRegisterOrLogin,
              state: {
                next: `${props.location.pathname}${props.location.search}`,
              },
            }}
          />
        )
      }}
    />
  )
}

class UberRouter extends React.PureComponent {
  componentDidMount() {
    const { injectReducer, injectSaga } = this.props

    injectReducer('keywordLists', KeywordListsReducer)
    injectSaga('keywordLists', KeywordListsSaga)
    injectReducer('siteAudit', SiteAuditReducer)
    injectSaga('siteAudit', SiteAuditSaga)
    injectReducer('adminDashboard', AdminDashboardReducer)
    injectSaga('adminDashboard', AdminDashboardSAga)
  }

  render() {
    const { user, languageCode, location } = this.props

    return (
      <Suspense fallback={<Loading />}>
        <Switch>
          <Redirect exact from="/login" to={`/${languageCode}/login`} />
          <Redirect exact from="/register" to={`/${languageCode}/register`} />
          <Redirect
            exact
            from="/verify-email"
            to={`/${languageCode}/verify-email`}
          />
          <Redirect exact from="/alerts" to={`/${languageCode}/alerts`} />
          <Redirect
            exact
            from="/alerts/:id"
            to={`/${languageCode}/alerts/:id`}
          />
          <Redirect
            exact
            from="/forgot-password"
            to={`/${languageCode}/forgot-password`}
          />
          <Redirect
            from="/change-password*"
            to={{ ...location, pathname: `/${languageCode}/change-password` }}
          />
          <Redirect exact from="/pricing" to={`/${languageCode}/pricing`} />
          <Redirect exact from="/todo" to={`/${languageCode}/todo`} />
          <Redirect
            exact
            from="/ubersuggest/overview"
            to={`/${languageCode}/ubersuggest/overview`}
          />
          <Redirect
            exact
            from="/ubersuggest/keyword_bulk_analysis"
            to={`/${languageCode}/ubersuggest/keyword_bulk_analysis`}
          />
          <Redirect
            exact
            from="/ubersuggest/keyword_ideas"
            to={`/${languageCode}/ubersuggest/keyword_ideas`}
          />
          <Redirect
            exact
            from="/ubersuggest/keyword_visualization"
            to={`/${languageCode}/ubersuggest/keyword_visualization`}
          />
          <Redirect
            exact
            from="/ubersuggest/keyword_lists/:id"
            to={`/${languageCode}/ubersuggest/keyword_lists/:id`}
          />
          <Redirect
            exact
            from="/ubersuggest/keyword_lists"
            to={`/${languageCode}/ubersuggest/keyword_lists`}
          />
          <Redirect
            exact
            from="/ubersuggest/content_ideas"
            to={`/${languageCode}/ubersuggest/content_ideas`}
          />
          <Redirect
            exact
            from="/traffic_analyzer/overview"
            to={`/${languageCode}/traffic_analyzer/overview`}
          />
          <Redirect
            exact
            from="/traffic_analyzer/top_pages"
            to={`/${languageCode}/traffic_analyzer/top_pages`}
          />
          <Redirect
            exact
            from="/traffic_analyzer/keywords"
            to={`/${languageCode}/traffic_analyzer/keywords`}
          />
          <Redirect
            exact
            from="/traffic_analyzer/competitors"
            to={`/${languageCode}/traffic_analyzer/competitors`}
          />
          <Redirect
            exact
            from="/seo_analyzer/site_audit"
            to={`/${languageCode}/seo_analyzer/site_audit`}
          />
          <Redirect
            exact
            from="/seo_analyzer/backlinks"
            to={`/${languageCode}/seo_analyzer/backlinks`}
          />
          <Redirect exact from="/checkout" to={`/${languageCode}/checkout`} />
          <Redirect exact from="/dashboard" to={`/${languageCode}/dashboard`} />
          <Redirect
            exact
            from="/position_tracking/:projectId"
            to={`/${languageCode}/position_tracking/:projectId`}
          />
          <Redirect
            exact
            from="/settings/:page"
            to={`/${languageCode}/settings/:page`}
          />
          <Redirect
            exact
            from="/admin/:page"
            to={`/${languageCode}/admin/:page`}
          />
          <Redirect
            exact
            from="/labs/landing"
            to={`/${languageCode}/labs/landing`}
          />
          <Redirect
            exact
            from="/labs/keywords_generator"
            to={`/${languageCode}/labs/keywords_generator`}
          />
          <Route
            exact
            path="/:languageCode/login"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={LOGIN} />
                  <TurnstileProvider
                    WrappedComponent={LoginContainer}
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/register"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={REGISTER} />
                  <TurnstileProvider
                    WrappedComponent={RegisterContainer}
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <AuthRoute
            exact
            user={user}
            path="/:languageCode/alerts"
            languageCode={languageCode}
            redirectTo="/dashboard"
            render={(props) => (
              <>
                <HelmetReact path={ALERTS} />
                <AlertsContainer {...props} user={user} />
              </>
            )}
          />
          <AuthRoute
            exact
            user={user}
            path="/:languageCode/alerts/:id"
            languageCode={languageCode}
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact
                    path={`alerts/${pathParams?.id}`}
                    pageKey={ALERTS_ID}
                  />
                  <AlertPage {...{ ...props, ...params, ...pathParams }} />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/forgot-password"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={FORGOT_PASSWORD} />
                <ForgotPasswordContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/change-password"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={CHANGE_PASSWORD} />
                <ChangePasswordContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/keyword_ideas"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={UBERSUGGEST_KEYWORD_IDEAS} />
                <NewKeywordIdeasContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/keyword_visualization"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={UBERSUGGEST_KEYWORD_VISUALIZATION} />
                <KeywordVisualizationContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/keyword_lists/:id"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact
                    path={`ubersuggest/keyword_lists/${pathParams?.id}`}
                    pageKey={KEYWORD_LISTS_ID}
                  />
                  <KeywordsContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/keyword_lists"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={UBERSUGGEST_KEYWORD_LISTS} />
                <KeywordListsContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/content_ideas"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={UBERSUGGEST_CONTENT_IDEAS} />
                <ContentIdeasContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/traffic_analyzer/overview"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={TRAFFIC_ANALYZER_OVERVIEW} />
                <TrafficAnalyzerOverviewContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/traffic_analyzer/top_pages"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={TRAFFIC_ANALYZER_TOP_PAGES} />
                <TopPagesContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/traffic_analyzer/keywords"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={TRAFFIC_ANALYZER_KEYWORDS} />
                <KeywordsByTrafficContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/traffic_analyzer/competitors"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={TRAFFIC_ANALYZER_COMPETITORS} />
                <CompetitorAnalysisContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/overview"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={UBERSUGGEST_OVERVIEW} />
                <KeywordOverviewContainer {...props} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/ubersuggest/keyword_bulk_analysis"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={UBERSUGGEST_BULK_ANALYSIS} />
                <BulkAnalysisContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/seo_analyzer/site_audit"
            render={(props) => {
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={SEO_ANALYZER_SITE_AUDIT} />
                  <SiteAuditContainer {...props} user={user} />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/seo_analyzer/backlinks"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={SEO_ANALYZER_BACKLINKS} />
                <BackLinksContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/seo_analyzer/backlink_opportunity"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={SEO_ANALYZER_BACKLINK_OPPORTUNITY} />
                <BackLinkOpportunityContainer {...props} user={user} />
              </ErrorBoundary>
            )}
          />
          <AuthRoute
            exact
            user={user}
            path="/:languageCode/checkout"
            languageCode={languageCode}
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={CHECKOUT} />
                  <CheckoutContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/pricing"
            render={(props) => {
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={PRICING} />
                  <PricingContainer {...props} />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/extension"
            render={(props) => {
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={EXTENSION} />
                  <ExtensionContainer {...props} />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/labs/landing"
            render={(props) => {
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={LABS_LANDING} />
                  <LabsContainer {...props} />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/labs/ai_writer"
            render={() => {
              window.location.replace(AIWRITER_BASE_URL)
              return null
            }}
          />
          <Route
            exact
            path="/:languageCode/labs/ai_writer"
            render={() => {
              window.location.replace(AIWRITER_BASE_URL)
              return null
            }}
          />
          <Route
            exact
            path="/:languageCode/labs/keywords_generator"
            languageCode={languageCode}
            render={(props) => {
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path="labs/keywords_generator" />
                  <KeywordsGeneratorContainer {...props} user={user} />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/dashboard"
            render={(props) => (
              <ErrorBoundary {...props}>
                <HelmetReact path={DASHBOARD} />
                <DashboardContainer {...props} />
              </ErrorBoundary>
            )}
          />
          <Route
            exact
            path="/:languageCode/position_tracking/:projectId"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact
                    path={`position_tracking/${pathParams?.projectId}`}
                    pageKey={POSITION_TRACKING_ID}
                  />
                  <RankTrackingContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/workspace"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={WORKSPACE} />
                  <WorkspaceContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/dashboard/competitor_tracking/:projectId"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact
                    path={`dashboard/competitor_tracking/${pathParams?.projectId}`}
                    pageKey={DASHBOARD_COMPETITORS_TRACKING_ID}
                  />
                  <CompetitorTrackingContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/seo_opportunities"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={SEO_OPPORTUNITIES} />
                  <SeoOpportunitiesContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <AuthRoute
            exact
            user={user}
            path="/:languageCode/settings/:page"
            languageCode={languageCode}
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact
                    path={`settings/${pathParams?.page}`}
                    pageKey={SETTINGS_ID}
                  />
                  <SettingsContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <AuthRoute
            exact
            user={user}
            path="/:languageCode/admin/:page"
            role="admin"
            redirectTo="/dashboard"
            languageCode={languageCode}
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <AdminContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <AuthRoute
            exact
            user={user}
            path="/:languageCode/admin/:page/:subpage"
            role="admin"
            redirectTo="/dashboard"
            languageCode={languageCode}
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <AdminContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/verify-email"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={VERIFY_EMAIL} />
                  <EmailVerificationPage
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            exact
            path="/:languageCode/enterprise/dashboard"
            render={(props) => {
              const params = parse(props.location.search || '')
              const pathParams = props.match.params
              return (
                <ErrorBoundary {...props}>
                  <HelmetReact path={ENTERPRISE_DASHBOARD} />
                  <AdministratorDashboardContainer
                    {...{ ...props, ...params, ...pathParams }}
                    user={user}
                  />
                </ErrorBoundary>
              )
            }}
          />
          <Route
            render={() => (
              <Redirect exact to={`/${languageCode}/ubersuggest/overview`} />
            )}
          />
        </Switch>
      </Suspense>
    )
  }
}
export default UberRouter
